import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { useToast } from '../components/ui/use-toast';
import {
  Plus, Pencil, Eye, Search, RefreshCw, Download, Save, ArrowLeft, Loader2,
} from 'lucide-react';
import { useRestaurant } from '../context/RestaurantContext';
import { useT } from '../context/LanguageContext';
import {
  listTaxLabels, createTaxLabel, updateTaxLabel,
} from '../services/taxRateService';

// Choices mirror TaxLabel: Mauritius taxes VAT, not GST, and anything else files under Other.
export const TAX_TYPES = [
  { value: 'VAT', label: 'VAT' },
  { value: 'SERVICE', label: 'Service Charge' },
  { value: 'OTHER', label: 'Other' },
];
const RATE_TYPES = [
  { value: 'PERCENT', label: 'Percentage' },
  { value: 'FLAT', label: 'Flat Amount' },
];
// MRA VAT 3 return boxes.
export const VAT_RETURN_BOXES = [
  { value: '', label: 'None' },
  { value: '1.1', label: '1.1 - Zero-rated supplies (Exports)' },
  { value: '1.2', label: '1.2 - Zero-rated supplies other than exports' },
  { value: '1.3', label: '1.3 - Taxable supplies to exempt bodies/persons' },
  { value: '1.4', label: '1.4 - Other taxable supplies' },
  { value: '2', label: '2 - Deferred VAT on importation' },
  { value: '3', label: '3 - Exempt supplies' },
  { value: '6.1', label: '6.1 - Capital goods imported' },
  { value: '6.2', label: '6.2 - Zero-rated imports' },
  { value: '6.3', label: '6.3 - Other imports' },
  { value: '6.4', label: '6.4 - Capital goods purchased locally' },
  { value: '6.5', label: '6.5 - Zero-rated goods/services purchased locally' },
  { value: '6.6', label: '6.6 - Other goods/services purchased locally' },
  { value: '7', label: '7 - Taxable input (no credit allowed)' },
  { value: '8.1', label: '8.1 - Exempt input - Imported goods' },
  { value: '8.2', label: '8.2 - Exempt input - Local goods/services' },
];

const StatusBadge = ({ active }) => (
  <span className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full ${
    active ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'
  }`}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const TypeChip = ({ type }) => (
  <span className="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-700">
    {TAX_TYPES.find((t) => t.value === type)?.label || type || '—'}
  </span>
);

const emptyForm = {
  name: '', rate: '', rate_type: 'PERCENT', tax_type: 'VAT',
  vat_return_box: '', is_compound: false, is_active: true, all_customers: false, description: '',
};

export default function TaxRatesManagement() {
  const { toast } = useToast();
  const { t } = useT();
  const { restaurantId } = useRestaurant();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');

  // null = list view; {} = new; {...row} = edit
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      setRows(await listTaxLabels(restaurantId));
    } catch {
      toast({ title: 'Error', description: 'Could not load tax rates', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [restaurantId, toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.name.toLowerCase().includes(q) || (r.tax_type || '').toLowerCase().includes(q));
  }, [rows, search]);

  const openNew = () => { setForm(emptyForm); setEditing({}); };
  const openEdit = (r) => {
    setForm({
      name: r.name,
      rate: r.current_rate ?? '',
      rate_type: r.rate_type || 'PERCENT',
      tax_type: r.tax_type || 'VAT',
      vat_return_box: r.vat_return_box || '',
      is_compound: !!r.is_compound,
      is_active: !!r.is_active,
      all_customers: !!r.all_customers,
      description: r.description || '',
    });
    setEditing(r);
  };

  /* Default = rides on every dish that names no tax of its own, on the
     kiosk and the bill alike (the server's `all_customers`). A partial
     update, so the rate and its dated history are untouched; with no rate
     marked, every active one applies. */
  const setDefault = async (r, on) => {
    try {
      await updateTaxLabel(restaurantId, r.id, { all_customers: on });
      toast({ title: t('settings.tax.defaultSaved') });
      load();
    } catch (e) {
      toast({ title: 'Error', description: String(e?.message || e), variant: 'destructive' });
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Validation', description: 'Name is required', variant: 'destructive' });
      return;
    }
    if (form.rate === '' || Number.isNaN(Number(form.rate)) || Number(form.rate) < 0) {
      toast({ title: 'Validation', description: 'Enter a valid rate', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim(), rate: Number(form.rate) };
      if (editing?.id) await updateTaxLabel(restaurantId, editing.id, payload);
      else await createTaxLabel(restaurantId, payload);
      toast({ title: editing?.id ? 'Tax rate updated' : 'Tax rate created' });
      setEditing(null);
      load();
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e?.response?.data?.error || e.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    const head = 'Name,Type,Rate,Rate Type,Compound,VAT Return Box,Status\n';
    const body = rows.map((r) => [
      r.name, r.tax_type, r.current_rate ?? '', r.rate_type,
      r.is_compound ? 'Yes' : 'No', r.vat_return_box || '',
      r.is_active ? 'Active' : 'Inactive',
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([head + body], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'tax-rates.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const selectCls =
    'w-full h-10 rounded-md border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:border-emerald-500';

  // ---------------- form view (new / edit) ----------------
  if (editing !== null) {
    return (
      <div className="p-6 w-full">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setEditing(null)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">{editing?.id ? 'Edit Tax Rate' : 'New Tax Rate'}</h1>
          </div>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
            Save
          </Button>
        </div>

        <Card>
          <CardContent className="pt-6">
            <h2 className="font-semibold mb-4">Tax Rate Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Name <span className="text-red-500">*</span></Label>
                <Input className="mt-1" placeholder="e.g. VAT 15%" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>Rate (%) <span className="text-red-500">*</span></Label>
                <Input className="mt-1" type="number" min="0" step="0.0001" value={form.rate}
                  onChange={(e) => setForm({ ...form, rate: e.target.value })} />
              </div>
              <div>
                <Label>Type</Label>
                <select className={`${selectCls} mt-1`} value={form.rate_type}
                  onChange={(e) => setForm({ ...form, rate_type: e.target.value })}>
                  {RATE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Tax Type</Label>
                <select className={`${selectCls} mt-1`} value={form.tax_type}
                  onChange={(e) => setForm({ ...form, tax_type: e.target.value })}>
                  {TAX_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <Label>VAT Return Box (MRA VAT 3)</Label>
                <select className={`${selectCls} mt-1`} value={form.vat_return_box}
                  onChange={(e) => setForm({ ...form, vat_return_box: e.target.value })}>
                  <option value="" disabled>Select…</option>
                  {VAT_RETURN_BOXES.map((b) => <option key={b.value || 'none'} value={b.value}>{b.label}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-6 mt-4">
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_compound}
                  onChange={(e) => setForm({ ...form, is_compound: e.target.checked })} />
                Compound tax
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                Active
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.all_customers}
                  onChange={(e) => setForm({ ...form, all_customers: e.target.checked })} />
                {t('settings.tax.defaultForm')}
              </label>
            </div>
            <Textarea className="mt-4" rows={3} placeholder="Description" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------------- list view ----------------
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">Tax Rates</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv}>
            <Download className="h-4 w-4 mr-1.5" /> Export
          </Button>
          <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4 mr-1.5" /> New Tax Rate
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input className="pl-9" placeholder="Search…" value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setSearch(query)} />
        </div>
        <Button variant="outline" onClick={() => setSearch(query)}>Search</Button>
        <Button variant="ghost" size="icon" onClick={() => { setQuery(''); setSearch(''); load(); }}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* What "Default" means here, said once: the rates every dish without a
          tax of its own carries, on the kiosk and the bill alike. */}
      <p className="mb-3 text-sm text-gray-500">
        {t('settings.tax.defaultHint')}
        {rows.length > 0 && !rows.some((r) => r.all_customers && r.is_active)
          ? ` ${t('settings.tax.defaultNone')}` : ''}
      </p>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tax Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Rate (%)</TableHead>
              <TableHead>Compound</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>{t('settings.tax.default')}</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-gray-400">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-gray-400">
                No tax rates yet — click <strong>New Tax Rate</strong> to add one.
              </TableCell></TableRow>
            ) : filtered.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell><TypeChip type={r.tax_type} /></TableCell>
                <TableCell className="text-right">
                  {r.current_rate != null
                    ? (r.rate_type === 'FLAT'
                      ? `Rs ${Number(r.current_rate).toFixed(2)}`
                      : `${Number(r.current_rate).toFixed(4)}%`)
                    : <span className="text-gray-400">no rate</span>}
                </TableCell>
                <TableCell>{r.is_compound ? 'Yes' : 'No'}</TableCell>
                <TableCell><StatusBadge active={r.is_active} /></TableCell>
                <TableCell>
                  <button type="button" aria-pressed={!!r.all_customers}
                    onClick={() => setDefault(r, !r.all_customers)}
                    title={r.all_customers ? t('settings.tax.defaultClear') : t('settings.tax.makeDefault')}
                    className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full border transition ${
                      r.all_customers
                        ? 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700'
                        : 'border-gray-200 bg-white text-gray-500 hover:border-emerald-400 hover:text-emerald-700'}`}>
                    {r.all_customers ? t('settings.tax.defaultBadge') : t('settings.tax.makeDefault')}
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => setViewing(r)}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(r)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <p className="text-sm text-gray-400 mt-3">
        Showing {filtered.length ? `1-${filtered.length}` : 0} of {filtered.length}
      </p>

      {/* Read-only view */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{viewing?.name}</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Tax Type</span><TypeChip type={viewing.tax_type} /></div>
              <div className="flex justify-between"><span className="text-gray-500">Rate</span>
                <span>{viewing.current_rate != null
                  ? (viewing.rate_type === 'FLAT' ? `Rs ${viewing.current_rate}` : `${viewing.current_rate}%`)
                  : '—'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Rate Type</span>
                <span>{RATE_TYPES.find((t) => t.value === viewing.rate_type)?.label}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Compound</span><span>{viewing.is_compound ? 'Yes' : 'No'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">VAT Return Box</span>
                <span>{VAT_RETURN_BOXES.find((b) => b.value === viewing.vat_return_box)?.label || 'None'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Status</span><StatusBadge active={viewing.is_active} /></div>
              <div className="flex justify-between"><span className="text-gray-500">{t('settings.tax.default')}</span>
                <span>{viewing.all_customers ? t('settings.tax.yes') : t('settings.tax.no')}</span></div>
              {viewing.description && <p className="text-gray-600 pt-2 border-t">{viewing.description}</p>}
              {(viewing.rates || []).length > 1 && (
                <div className="pt-2 border-t">
                  <p className="text-gray-500 mb-1">Rate history</p>
                  {viewing.rates.map((h) => (
                    <div key={h.id} className="flex justify-between text-xs text-gray-500">
                      <span>{h.start_date} → {h.end_date || 'open'}</span>
                      <span>{h.rate}% · {h.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewing(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
